import { gql, GraphQLClient as GraphQLClientBase, request } from "graphql-request"
import jwtDecode from "jwt-decode"
import env from "../../env"
import { Axios as AxiosBase } from "axios"
import { JwtUser } from "@navigate/common/src/types/user.types"
import { toast } from "react-hot-toast"

const initAccessToken = ["undefined", "null", null].includes(localStorage.getItem("accessToken"))
  ? ""
  : localStorage.getItem("accessToken")
const initRefreshToken = ["undefined", "null", null].includes(localStorage.getItem("refreshToken"))
  ? ""
  : localStorage.getItem("refreshToken")

/*
  ======= Gql Clients  =======================================
*/
export const gqlEndpoint = env.apiEndpoint + "/graphql"

export class GraphQLClient extends GraphQLClientBase {
  refreshToken: string | null = initRefreshToken

  async refreshingRequest(...args: any[]) {
    //  get the expiry time of the token
    if (this.refreshToken) {
      //  get the expiry time of the token
      // @ts-ignore
      const jwtPayload: { exp: number } = jwtDecode(this.options?.headers?.authorization?.split(" ")?.[1]) || 0
      const expiryTime = new Date(jwtPayload.exp * 1000)

      //  if the token has expired
      if (expiryTime < new Date()) {
        console.info("GraphQLClient Refreshing...")
        const tk = await refreshTokens(this.refreshToken)
        //  set the new access token
        this.setHeader("authorization", `Bearer ${tk.accessToken}`)
        this.refreshToken = tk.refreshToken
      }
    }

    let result
    try {
      // @ts-ignore TS-2556
      result = await this.request(...args)
    } catch (e: any) {
      console.log(e)
      const status = e?.response?.status
      if (status === 401 || status === 403) {
        logoutRemoveTokens()
        window.location.href = "/account"
        return
      }
      toast.error(e?.response?.errors?.map((e1: any) => e1.message).join("\n"))
    }
    return result
  }
}

/*
  ======= Axios / http client =======================================
*/
export class Axios extends AxiosBase {
  refreshToken: string | null = initRefreshToken
}

export const httpClient = new Axios({
  baseURL: env.apiEndpoint,
  headers: {
    accept: "application/json",
    authorization: `Bearer ${initAccessToken}`,
  },
  transformResponse: [
    // https://github.com/axios/axios/issues/3419
    // "application/json" is only part of the header. also includes "charset=utf-8"
    (data, headers) => {
      return data
    },
  ],
})

// intercept the httpClient to  refresh token before expiry
httpClient.interceptors.request.use(async (config) => {
  if (httpClient.refreshToken) {
    if (!config.headers?.authorization) {
      // return Promise.reject("Not authenticated. Use public Http client for un-authenticated requests.")
      return config // pass
    }

    //  get the expiry time of the token
    // TODO: below added by AI. why?
    const token = config.headers?.authorization?.split(" ")?.[1]
    if (!token) {
      return config // pass if no valid token
    }

    const decodedToken = jwtDecode(token)
    if (!decodedToken || typeof decodedToken !== "object" || !("exp" in decodedToken)) {
      return config // pass if token can't be decoded or lacks expiry
    }

    const jwtPayload = decodedToken as { exp: number }
    // TODO: above added by AI. why?
    const expiryTime = new Date(jwtPayload.exp * 1000)

    //  if the token has expired
    if (expiryTime < new Date()) {
      const tk = await refreshTokens(httpClient.refreshToken)
      //  set the new access token
      if (!config.headers) {
        config.headers = {}
      }
      config.headers.authorization = `Bearer ${tk.accessToken}`
      httpClient.refreshToken = tk.refreshToken
      return config
    }
  }

  return config
}, Promise.reject)

// Response interceptor: catch 401/403 and force logout + redirect
httpClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status
    if (status === 401 || status === 403) {
      logoutRemoveTokens()
      window.location.href = "/account"
    }
    return Promise.reject(error)
  },
)

export const graphqlClient = new GraphQLClient(`${gqlEndpoint}`, {
  headers: {
    authorization: `Bearer ${initAccessToken}`,
    "apollo-require-preflight": "true",
  },
})

export const graphqlGetClient = new GraphQLClient(`${gqlEndpoint}`, {
  method: "GET",
  headers: {
    authorization: `Bearer ${initAccessToken}`,
    "apollo-require-preflight": "true",
  },
})

const refreshTokens = async (refreshToken: string) => {
  try {
    const response = await request(
      gqlEndpoint,
      gql`
        mutation refresh($refreshToken: String!) {
          refresh(refreshToken: $refreshToken) {
            accessToken
            # refreshToken
            user {
              id
              email
              isPremium
            }
          }
        }
      `,
      { refreshToken },
      {
        "apollo-require-preflight": "true",
      },
    )

    const tk = {
      accessToken: response.refresh.accessToken,
      refreshToken: response.refresh.refreshToken || refreshToken,
      user: response.refresh.user,
    }

    // update http clients / localstorage
    if (tk.accessToken) {
      graphqlClient.setHeaders({
        authorization: `Bearer ${tk.accessToken}`,
        "apollo-require-preflight": "true",
      })
      graphqlGetClient.setHeaders({
        authorization: `Bearer ${tk.accessToken}`,
        "apollo-require-preflight": "true",
      })
      if (!httpClient.defaults.headers.common) {
        httpClient.defaults.headers.common = {}
      }
      httpClient.defaults.headers.common["authorization"] = `Bearer ${tk.accessToken}`
      localStorage.setItem("accessToken", tk.accessToken)
    }
    if (tk.refreshToken) {
      httpClient.refreshToken = tk.refreshToken
      graphqlClient.refreshToken = refreshToken
      graphqlGetClient.refreshToken = refreshToken
      localStorage.setItem("refreshToken", tk.refreshToken)
    }
    if (tk.user) {
      localStorage.setItem("user", JSON.stringify(tk.user))
    }

    return tk
  } catch (e) {
    // refresh token invalid or expired — force full logout + redirect
    logoutRemoveTokens()
    window.location.href = "/account"
    return {
      accessToken: null,
      refreshToken: null,
    }
  }
}

export type LoginInput = {
  user: JwtUser
  accessToken: string
  refreshToken: string
}

// update local storage
export const loginSetTokens = ({ user, accessToken, refreshToken }: LoginInput) => {
  localStorage.setItem("accessToken", accessToken)
  localStorage.setItem("refreshToken", refreshToken)
  localStorage.setItem("user", JSON.stringify(user))
  // https://stackoverflow.com/questions/61000058/change-in-localstorage-not-triggering-event-listener
  window.dispatchEvent(new Event("storage"))

  // update http clients
  graphqlClient.setHeaders({
    authorization: `Bearer ${accessToken}`,
    "apollo-require-preflight": "true",
  })
  graphqlClient.refreshToken = refreshToken

  graphqlGetClient.setHeaders({
    authorization: `Bearer ${accessToken}`,
    "apollo-require-preflight": "true",
  })
  graphqlGetClient.refreshToken = refreshToken

  if (!httpClient.defaults.headers.common) {
    httpClient.defaults.headers.common = {}
  }
  httpClient.defaults.headers.common["authorization"] = `Bearer ${accessToken}`
  httpClient.refreshToken = refreshToken
}

export const logoutRemoveTokens = () => {
  graphqlClient.setHeaders({
    authorization: "",
    "apollo-require-preflight": "true",
  })
  graphqlGetClient.setHeaders({
    authorization: "",
    "apollo-require-preflight": "true",
  })
  if (httpClient.defaults.headers?.common?.["authorization"]) {
    httpClient.defaults.headers.common["authorization"] = ""
  }

  graphqlClient.refreshToken = null
  graphqlGetClient.refreshToken = null
  httpClient.refreshToken = null

  localStorage.removeItem("accessToken")
  localStorage.removeItem("refreshToken")
  localStorage.removeItem("user")
  // https://stackoverflow.com/questions/61000058/change-in-localstorage-not-triggering-event-listener
  window.dispatchEvent(new Event("storage"))
}
